import { useCart } from '../context/CartContext';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function CartDrawer() {
  const { 
    cartItems, isCartOpen, closeCart,
    removeFromCart, updateQuantity,
    cartTotal 
  } = useCart();
  const navigate = useNavigate();
  const [maxStockItems, setMaxStockItems] = useState({});

  const handleUpdateQuantity = async (itemId, newQuantity, item) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
      return;
    }
    const availableStock = Number(item.stock_quantity);
    if (availableStock && newQuantity > availableStock) {
      setMaxStockItems(prev => ({ ...prev, [itemId]: true }));
      setTimeout(() => setMaxStockItems(
        prev => ({ ...prev, [itemId]: false }))
      , 3000);
      return;
    }
    await updateQuantity(itemId, newQuantity);
  };

  const displayItems = cartItems.filter(item => !item.isGiftCard);

  const drawerClass = `fixed top-0 right-0 h-full w-full max-w-md bg-white z-[1002] shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`;

  return (
    <>
      {/* Overlay */}
      {isCartOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[998] transition-opacity"
          onClick={closeCart}
        />
      )}

      {/* Drawer */}
      <div className={drawerClass}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">
            Shopping Bag
            {displayItems.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({displayItems.length} items)
              </span>
            )}
          </h2>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <svg className="w-16 h-16 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <p className="text-gray-500 font-medium">
                Your bag is empty
              </p>
              <button
                onClick={closeCart}
                className="px-6 py-2 bg-gold text-white rounded-lg font-semibold text-sm hover:brightness-90 transition"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {displayItems.map((item, index) => (
                <div key={index} className="flex gap-3 border-b pb-4">
                  {/* Image */}
                  <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 uppercase truncate">
                      {item.brand}
                    </p>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.name}
                    </p>
                    {(item.color || item.size) && (
                      <p className="text-xs text-gray-400">
                        {[item.color, item.size]
                          .filter(Boolean)
                          .join(' / ')}
                      </p>
                    )}
                    <p className="text-sm font-bold text-gold mt-1">
                      KSh {Number(item.unit_price).toLocaleString()}
                    </p>

                    {/* Quantity + Remove */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 border rounded-lg overflow-hidden">
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1, item)}
                          className="px-2 py-1 hover:bg-gray-100 text-gray-600 text-sm"
                        >
                          -
                        </button>
                        <span className="px-2 text-sm font-medium">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1, item)}
                          className="px-2 py-1 hover:bg-gray-100 text-gray-600 text-sm"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-xs text-red-400 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                    {maxStockItems[item.id] && (
                      <p className="text-xs text-red-500 mt-1">
                        Max stock reached
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {displayItems.length > 0 && (
          <div className="border-t p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Subtotal</span>
              <span className="font-bold text-gray-900 text-lg">
                KSh {Number(cartTotal).toLocaleString()}
              </span>
            </div>
            <button
              onClick={() => {
                closeCart();
                navigate('/checkout');
              }}
              className="w-full py-3 bg-gold text-white rounded-lg font-semibold hover:brightness-90 transition"
            >
              Proceed to Checkout
            </button>
            <Link
              to="/cart"
              onClick={closeCart}
              className="block text-center text-sm text-gray-500 hover:text-gray-700"
            >
              View Full Cart
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
